import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { LuBadgeCheck, LuKeyRound, LuTrash2 } from 'react-icons/lu';

import { AiCredential } from '../../entities/ai-credential';
import { CODE_AGENT_PROVIDER_LABEL, CodeAgentProvider } from '../../entities/code-agent-task';
import { aiCredentialErrorMessage, deleteAiCredential, saveAiCredential } from '../../shared/api/ai-credentials';

interface AiProviderIntegrationProps {
  provider: CodeAgentProvider;
  credential?: AiCredential;
  onUpdate: () => void;
}

// One BYOK provider's connect/manage body — rendered as a tile's modal content on the
// Integrations page (widgets/integrations/IntegrationCard.tsx already supplies the icon/title/
// status header, so this is body-only, same convention as Telegram/Slack/Email). A saved key is
// encrypted at rest and never shown in full again once saved — only replace or remove.
const AiProviderIntegration: React.FC<AiProviderIntegrationProps> = ({ provider, credential, onUpdate }) => {
  const [apiKey, setApiKey] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const label = CODE_AGENT_PROVIDER_LABEL[provider];

  const handleSave = async () => {
    if (!apiKey.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      const { verified } = await saveAiCredential(provider, apiKey.trim());
      setApiKey('');
      onUpdate();
      toast.success(verified ? `${label} key saved and verified.` : `${label} key saved.`);
    } catch (err) {
      setError(aiCredentialErrorMessage(err) ?? 'Failed to save the API key.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      await deleteAiCredential(provider);
      onUpdate();
      toast.success(`${label} key removed.`);
    } catch (err) {
      setError(aiCredentialErrorMessage(err) ?? 'Failed to remove the API key.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-3">
      {error && <div className="text-error text-sm">{error}</div>}

      {credential ? (
        <div className="flex items-center justify-between gap-3 rounded-lg bg-base-200 px-3 py-2.5">
          <div className="flex items-center gap-2 min-w-0">
            <LuKeyRound className="opacity-60 shrink-0" />
            <span className="text-sm font-mono">•••• {credential.keyHint}</span>
            {credential.verified && (
              <span
                className="badge badge-success badge-xs gap-1"
                title="mFlow confirmed this key works with the provider"
              >
                <LuBadgeCheck size={10} /> Verified
              </span>
            )}
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-xs text-error shrink-0"
            onClick={handleDelete}
            disabled={isDeleting}
            title={`Remove ${label} key`}
          >
            {isDeleting ? <span className="loading loading-spinner loading-xs" /> : <LuTrash2 size={14} />}
          </button>
        </div>
      ) : (
        <p className="text-sm opacity-70">
          Bring your own {label} key to run coding-agent tasks without mFlow&apos;s monthly hosted task limit.
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
        <label className="form-control w-full flex-1">
          <span className="label-text text-xs mb-1 opacity-60">{credential ? 'Replace API key' : 'API key'}</span>
          <input
            type="password"
            className="input input-bordered input-sm w-full"
            placeholder={credential ? 'Enter a new key to replace the saved one' : 'Paste your API key'}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            autoComplete="off"
          />
        </label>
        <button
          type="button"
          className="btn btn-primary btn-sm shrink-0"
          disabled={!apiKey.trim() || isSaving}
          onClick={handleSave}
        >
          {isSaving ? <span className="loading loading-spinner loading-xs" /> : 'Save'}
        </button>
      </div>
    </div>
  );
};

export default AiProviderIntegration;
